import React, { FC, ReactElement, useMemo, useState } from 'react'
import { ClickAwayListener } from '@material-ui/core'
import { ReactState } from '../../../../React/Common/Contexts'
import { IsStringEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import PopperBubble from '../../../../React/Common/Components/PopperBubble'

const MAX_VISIBLE_DEVICE_LIST_USERS: number = 2
const DEVICE_LIST_USERS_SEPARATOR: string = '\n'

export type DeviceListUsersCellProps = {
    users: string
    noneLabel: string
    moreUsersLabel: string
}

/**
 * <p>
 * Device list users cell: stack up to two users inline and show a popover for the complete list when more exist.
 * </p>
 *
 * @param props - Display values for the users cell.
 * @returns Cell content for the users column.
 */
export const DeviceListUsersCell: FC<DeviceListUsersCellProps> = ({
    users,
    noneLabel,
    moreUsersLabel
}: DeviceListUsersCellProps): ReactElement => {
    const [anchorEl, setAnchorEl]: ReactState<Nullable<HTMLElement>> = useState<Nullable<HTMLElement>>(null)

    const resolvedUsers: string[] = useMemo(
        (): string[] =>
            users.split(DEVICE_LIST_USERS_SEPARATOR).filter((user: string): boolean => !IsStringEmpty(user.trim())),
        [users]
    )

    if (ArrayUtils.isEmpty(resolvedUsers)) {
        return <span>{noneLabel}</span>
    }

    const visibleUsers: string[] = resolvedUsers.slice(0, MAX_VISIBLE_DEVICE_LIST_USERS)
    const overflowUsers: string[] = resolvedUsers.slice(MAX_VISIBLE_DEVICE_LIST_USERS)
    const hasOverflowUsers: boolean = resolvedUsers.length > MAX_VISIBLE_DEVICE_LIST_USERS

    return (
        <div className='device-list-users-cell'>
            {visibleUsers.map(
                (user: string): ReactElement => (
                    <div key={user} className='device-list-users-cell__entry'>
                        {user}
                    </div>
                )
            )}
            {hasOverflowUsers ? (
                <>
                    <div
                        className='device-list-users-cell__more router-datatable-body-clickable'
                        onClick={(event: React.MouseEvent<HTMLElement>): void => setAnchorEl(event.currentTarget)}
                    >
                        {moreUsersLabel}
                    </div>
                    <PopperBubble
                        open={isVarNotEmpty(anchorEl)}
                        anchorEl={anchorEl}
                        infoMode
                        placement='top'
                        content={
                            <ClickAwayListener onClickAway={(): void => setAnchorEl(null)}>
                                <div className='device-list-users-popover'>
                                    {overflowUsers.map(
                                        (user: string): ReactElement => (
                                            <div key={`popover-${user}`} className='device-list-users-popover__entry'>
                                                {user}
                                            </div>
                                        )
                                    )}
                                </div>
                            </ClickAwayListener>
                        }
                    />
                </>
            ) : null}
        </div>
    )
}

/**
 * Serialises the backend users array for storage in the data table row model.
 *
 * @param users - Backend users list from the device list API.
 * @returns Newline-delimited value consumed by {@link DeviceListUsersCell}.
 */
export const serialiseDeviceListUsers = (users: Definable<string[]>): string =>
    (users ?? []).join(DEVICE_LIST_USERS_SEPARATOR)
